import axios, { AxiosProgressEvent, CancelTokenSource, ResponseType } from 'axios'
import getDefaultAPIHeader from 'utils/http/getDefaultAPIHeader'
import formatHttpResponseError from 'utils/http/formatHttpResponseError'
import { INSTITUTION_API_URL } from 'constants/system'
import { removeEmptyObjectsFromFilter } from 'utils/api'

/* -------------------------------------------------------------------------- */
/* Instância básica do Axios                                                  */
/* -------------------------------------------------------------------------- */
const api = axios.create({
  baseURL: INSTITUTION_API_URL,
  timeout: 500000,
})

/* -------------------------------------------------------------------------- */
/* Mapas de controle                                                          */
/* -------------------------------------------------------------------------- */
const cancelTokens: Record<string, CancelTokenSource> = {}
const activeRequests: Record<string, string> = {}
const latestPromises: Record<string, Promise<any>> = {}

type AxiosProps = {
  method: string
  url: string
  timeout?: number
  data?: any
  responseType?: ResponseType
  onUploadProgress?: (e: AxiosProgressEvent) => void
  ignoreCancel?: (error: any) => void
}

/**
 * Chamada HTTP com:
 * 1. Cancelamento inteligente de duplicadas.
 * 2. Descarte de respostas obsoletas **sem quebrar** cadeias de `await`.
 * 3. Retorno do *último* resultado válido, evitando telas “vazias” interinas.
 */
export default function institutionAPI<T = any>(axiosConfig: AxiosProps): Promise<T | undefined> {
  /* ------------ Preparação síncrona antes de construir a Promise ---------- */
  const { url } = axiosConfig
  const requestId = `${url}_${Date.now()}`
  activeRequests[url] = requestId

  /* Cancela requisição anterior (se existir) */
  if (cancelTokens[url]) {
    cancelTokens[url].cancel('Canceled due to new request')
  }

  const cancelTokenSource = axios.CancelToken.source()
  cancelTokens[url] = cancelTokenSource

  /* ----------------------------- PROMISE ---------------------------------- */
  const promise: Promise<T | undefined> = (async () => {
    try {
      /* Cabeçalhos + sanitização de filtros */
      const headers = await getDefaultAPIHeader()

      if (axiosConfig?.data?.filter) {
        axiosConfig.data = await removeEmptyObjectsFromFilter(axiosConfig.data)
      }

      /* ------------------------- Chamada Axios ----------------------------- */
      const response = await api({
        headers,
        ...axiosConfig,
        cancelToken: cancelTokenSource.token,
      })

      /* Se não for a chamada mais recente, repasse a Promise nova              */
      if (activeRequests[url] !== requestId) {
        return latestPromises[url]
      }

      return response.data
    } catch (error: any) {
      /* ---------- Cancelamento explícito: devolve a Promise mais nova ------ */
      if (axios.isCancel(error)) {
        if (axiosConfig.ignoreCancel) axiosConfig.ignoreCancel(error)
        return latestPromises[url]
      }

      /* ------------------------ Demais erros ------------------------------ */
      return formatHttpResponseError(error)
    } finally {
      /* ------------------------- Limpeza ---------------------------------- */
      if (cancelTokens[url] === cancelTokenSource) delete cancelTokens[url]
      // if (activeRequests[url] === requestId) delete activeRequests[url]
    }
  })()

  /* Guarda a Promise como “a mais recente” para o endpoint                    */
  latestPromises[url] = promise
  return promise
}
